// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import useScrollSpy from "../hooks/useScrollSpy";

import {
  Menu,
  X,
  ArrowRight,
  Waves,
  Mail,
  Phone,
  MapPin,
  Globe,
  ShieldCheck,
  Sparkles,
  CreditCard,
  Lock,
  Activity,
} from "lucide-react";
import HeroVisual from "./HeroVisual";
import SocialIcons from "./SocialIcons";
import CertificationsSection from "./CertificationsSection";
import { trackEvent } from "../analytics/ga4";

const colors = {
  bgTop: "#080b1a",
  bgBottom: "#0d1127",
  primary: "#19E6FF",
  primaryAlt: "#A2F6FF",
  accentBlue: "#38bdf8",
  accentGreen: "#8CFBDE",
  accentPurple: "#C084FC",
  cardBg: "rgba(15, 23, 42, 0.45)",
  cardBorder: "rgba(255, 255, 255, 0.12)",
  text: "#f0f9ff",
  textMuted: "#94a3b8",
};

const useSmoothScroll = () => {
  useEffect(() => {
    const handler = (e) => {
      const el = e.target instanceof Element ? e.target : null;
      const target = el ? el.closest('a[href^="#"]') : null;
      if (!target) return;
      const id = target.getAttribute("href");
      if (id && id.startsWith("#")) {
        e.preventDefault();
        const el = document.querySelector(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);
};

// Handles URL hash on initial mount — fixes navigation from other pages like /pacevibe → /#contact
const useScrollToHashOnMount = () => {
  useEffect(() => {
    if (!window.location.hash) return;

    // Wait two animation frames so the DOM + framer-motion sections are laid out,
    // otherwise scrollIntoView lands on the wrong section.
    const id = window.location.hash.slice(1);
    let attempts = 0;
    const tryScroll = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempts < 10) {
        attempts++;
        setTimeout(tryScroll, 100);
      }
    };
    // Initial small delay for the page to render
    setTimeout(tryScroll, 150);
  }, []);
};

const Section = ({ id, className = "", children }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-10%" }}
    transition={{ duration: 0.8, ease: "easeOut" }}
    className={`w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}
  >
    {children}
  </motion.section>
);

// Framer Motion variants
const fmContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const fmItem = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

/* ─── i18n dictionary ─── */
const dict = {
  el: {
    // Nav
    navAbout: "Ποιοι είμαστε",
    navCertifications: "Πιστοποιήσεις",
    navProducts: "Εφαρμογές",
    navCaseStudies: "Case studies",
    navBlog: "Blog",
    navTrust: "Εμπιστοσύνη & Ασφάλεια",
    navContact: "Επικοινωνία",
    // Hero
    heroBadge: "Custom Software • AI Automation • Cloud",
    heroTitle1: "Λογισμικό και AI αυτοματισμοί που εξελίσσουν ",
    heroTitle2: "την επιχείρησή σας",
    heroDesc: "Η Orion Intelligence σχεδιάζει και υλοποιεί αξιόπιστες ψηφιακές λύσεις για επιχειρήσεις και οργανισμούς: web εφαρμογές, CRM/ERP dashboards, αυτοματοποίηση εγγράφων, legal tech, mobile apps και ασφαλείς cloud υποδομές.",
    heroDescHtml: true,
    heroTagline: "Από την επιχειρησιακή ανάγκη έως ένα ασφαλές, λειτουργικό προϊόν σε παραγωγή.",
    heroServices: [
      { label: "Custom εφαρμογές", href: "/custom-software" },
      { label: "AI αυτοματισμοί", href: "/ai-automations" },
      { label: "CRM / ERP dashboards", href: "/custom-software" },
      { label: "Invoice automation", href: "/invoice-automation" },
      { label: "Legal tech", href: "/legal-tech" },
      { label: "Mobile apps", href: "/mobile-apps" },
    ],
    heroCta1: "Ζητήστε πρόταση",
    heroCta2: "Δείτε παραδείγματα έργων",
    heroTechLabel: "Τεχνολογίες που χρησιμοποιώ",
    // About
    aboutTitle: "Γιατί Orion Intelligence;",
    aboutP1: "Στην Orion Intelligence βοηθάω επιχειρήσεις, επαγγελματίες και ομάδες να αποκτήσουν δικό τους λογισμικό αντί να προσαρμόζονται σε έτοιμα εργαλεία που δεν τους ταιριάζουν.",
    aboutP2: "Αναλαμβάνω από MVP και εσωτερικά dashboards μέχρι AI αυτοματισμούς, invoice recognition, legal tech προϊόντα και cloud υποδομές. Με έδρα τη Χαλκιδική και remote συνεργασίες σε Ελλάδα και Ευρώπη.",
    cardWebAppsTitle: "Custom Web Apps",
    cardWebAppsText: "SaaS πλατφόρμες, Dashboards, CRM/ERP συστήματα κομμένα και ραμμένα στα μέτρα σας.",
    cardAiTitle: "AI & Automation",
    cardAiText: "Ενσωμάτωση AI μοντέλων, Chatbots, αυτοματοποίηση ροών εργασίας και ανάλυση δεδομένων.",
    cardCloudTitle: "Cloud Infra & Security",
    cardCloudText: "Ασφαλείς υποδομές σε AWS/Google Cloud, GDPR compliance και προστασία δεδομένων.",
    cardMobileTitle: "Mobile Solutions",
    cardMobileText: "Native και Cross-platform εφαρμογές που προσφέρουν κορυφαία εμπειρία χρήστη.",
    specTitle: "Εξειδίκευση σε κλάδους",
    specLegalTitle: "Legal Tech", specLegalText: "Οργάνωση γραφείου, CRM δικηγόρων, ασφάλεια δεδομένων.",
    specSupplyTitle: "Supply Chain & Retail", specSupplyText: "Διαχείριση αποθεμάτων, B2B παραγγελίες.",
    specHealthTitle: "Health & Sports", specHealthText: "Performance tracking, ιατρικά δεδομένα.",
    specRealEstateTitle: "Real Estate", specRealEstateText: "Property management, αυτοματοποιημένες μισθώσεις.",
    // Products
    productsTitle: "Οι εφαρμογές μας",
    caseStudiesTitle: "Case studies",
    caseStudiesDesc: "Παραδείγματα έργων και use cases που δείχνουν πώς η Orion Intelligence μετατρέπει επιχειρησιακές ανάγκες σε λογισμικό, AI αυτοματισμούς και cloud προϊόντα.",
    caseStudyCards: [
      {
        title: "CaseMaster legal tech platform",
        text: "Οργάνωση υποθέσεων, πελατών και προθεσμιών για νομικά γραφεία με ασφαλή cloud αρχιτεκτονική.",
        href: "/case-studies/casemaster-legal-tech",
        tag: "Legal tech",
      },
      {
        title: "Invoice Intelligence automation",
        text: "AI ανάγνωση τιμολογίων, extraction δεδομένων και ERP-ready JSON για επιχειρήσεις και ERP providers.",
        href: "/case-studies/invoice-intelligence",
        tag: "Invoice automation",
      },
      {
        title: "AI legal workflow",
        text: "AI ροές εργασίας για ταξινόμηση αιτημάτων, επεξεργασία εγγράφων και εσωτερική οργάνωση ομάδων.",
        href: "/case-studies/ai-legal-workflow",
        tag: "AI automation",
      },
    ],
    blogTitle: "Blog",
    blogDesc: "Σύντομα άρθρα που εξηγούν με απλά λόγια πότε αξίζει το custom software, πώς βοηθούν οι AI αυτοματισμοί και πώς οργανώνεται καλύτερα μια επιχείρηση.",
    blogCta: "Δείτε όλα τα άρθρα",
    cmDesc: "Η απόλυτη λύση διαχείρισης για νομικούς. Οργανώστε υποθέσεις, πελάτες και προθεσμίες με την δύναμη της Τεχνητής Νοημοσύνης. Σχεδιασμένο για το σύγχρονο δικηγορικό γραφείο.",
    laDesc: "Η πλατφόρμα που αλλάζει τον τρόπο εύρεσης νομικών υπηρεσιών. Άμεση σύνδεση πολιτών με εξειδικευμένους δικηγόρους. Online ραντεβού και αξιολογήσεις.",
    themisDesc: "Πλατφόρμα AI Νομικής Σύνταξης με βάση τις πηγές του φακέλου. Διαβάζει δικαστικές αποφάσεις, έγγραφα και υποδείγματα DOCX και συντάσσει Εξουσιοδοτήσεις, Εκθέσεις Έρευνας & Δικόγραφα με πλήρη τεκμηρίωση.",
    ipDesc: "Ευέλικτη πλατφόρμα διαχείρισης συλλόγων και οργανισμών: μέλη, συνδρομές, προγράμματα, παρουσίες, έγγραφα, ανακοινώσεις και λειτουργικές αναφορές σε ένα ασφαλές περιβάλλον.",
    ipCaseStudy: "Πραγματική υλοποίηση: Σύλλογος Ιππόκαμπος ΑμεΑ",
    pvDesc: "Pro-grade race strategy και fueling για δρομείς. Pace splits, hour-by-hour διατροφικό πλάνο με συγκεκριμένα brands gels, heart-rate zones και lock-screen wallpaper με τα splits σου. Διαθέσιμο τώρα στο Google Play.",
    // Trust
    trustTitle: "Εμπιστοσύνη & Ασφάλεια",
    trustDesc: "Privacy-first, least-privilege, κρυπτογράφηση και ξεκάθαρες πολιτικές. Πληρωμές μέσω Stripe — δεν αποθηκεύουμε στοιχεία κάρτας.",
    trustTerms: "Όροι Χρήσης",
    trustPrivacy: "Πολιτική Απορρήτου",
    trustGdprTitle: "GDPR Συμμόρφωση", trustGdprText: "Συλλογή/επεξεργασία με νόμιμες βάσεις. Εργαλεία δικαιωμάτων υποκειμένου.",
    trustGdprDetails: ["Διαχείριση συγκατάθεσης", "Εξαγωγή/διαγραφή δεδομένων", "Διαφάνεια & τεκμηρίωση"],
    trustPayTitle: "Ασφαλείς Πληρωμές", trustPayText: "Οι πληρωμές διεκπεραιώνονται στη Stripe. Δεν αποθηκεύουμε στοιχεία κάρτας.",
    trustPayDetails: ["PCI-DSS συμμόρφωση", "3D Secure παντού", "Υπογεγραμμένα webhooks"],
    trustDataTitle: "Ασφάλεια Δεδομένων", trustDataText: "Firestore Rules, έλεγχος πρόσβασης και κρυπτογράφηση.",
    trustDataDetails: ["Least-privilege (RBAC)", "Encryption at rest/transit", "Audit logs & monitoring"],
    trustPrivacyTitle: "Ιδιωτικότητα by-default", trustPrivacyText: "Ελαχιστοποίηση δεδομένων & καθαρές ρυθμίσεις χρήστη.",
    trustPrivacyDetails: ["Privacy-first UI/flows", "Anonymized analytics", "User control settings"],
    trustReliabilityTitle: "Αξιοπιστία & Παρακολούθηση", trustReliabilityText: "Health checks, alerts και στρατηγική backup.",
    trustReliabilityDetails: ["Backups (Exports)", "Staging environments", "24/7 Monitoring"],
    trustRoadmapTitle: "Οδικός Χάρτης Συμμόρφωσης", trustRoadmapText: "Τακτικές αναθεωρήσεις και βέλτιστες πρακτικές.",
    trustRoadmapDetails: ["Review πολιτικών", "Εκπαίδευση συνεργατών", "Security code reviews"],
    // Contact
    contactTitle: "Επικοινωνία",
    contactLocation: "Πολύγυρος, Χαλκιδική — Remote-first συνεργασίες σε Ελλάδα & Ευρώπη",
    contactGdprNote: "Τα μηνύματά σας δεν αποθηκεύονται για marketing χωρίς συγκατάθεση. Θα χρησιμοποιηθούν αποκλειστικά για την απάντηση στο αίτημά σας.",
    formName: "Ονοματεπώνυμο",
    formNamePlaceholder: "Π.χ. Δημήτρης Δέλλιος",
    formEmail: "Email",
    formMessage: "Μήνυμα",
    formMessagePlaceholder: "Πες μας λίγα λόγια για αυτό που χρειάζεσαι…",
    formSubmit: "Αποστολή μηνύματος",
    formSubmitting: "Αποστολή...",
    formSuccess: "✅ Ελήφθη! Θα σου απαντήσουμε σύντομα.",
    formError: "❌ Κάτι πήγε στραβά. Δοκίμασε ξανά.",
    // Validation
    valName: "Συμπλήρωσε το όνομα.",
    valEmail: "Έγκυρο email, παρακαλώ.",
    valMessage: "Γράψε λίγες περισσότερες λεπτομέρειες (≥10 χαρακτήρες).",
    valBot: "Κάτι δεν πήγε σωστά. Δοκίμασε ξανά.",
    // Footer
    footerFollow: "Follow us",
  },
  en: {
    // Nav
    navAbout: "About Us",
    navCertifications: "Certifications",
    navProducts: "Products",
    navCaseStudies: "Case studies",
    navBlog: "Blog",
    navTrust: "Trust & Security",
    navContact: "Contact",
    // Hero
    heroBadge: "Custom Software • AI Automation • Cloud",
    heroTitle1: "Software and AI automation that move ",
    heroTitle2: "your business forward",
    heroDesc: "Orion Intelligence designs and delivers dependable digital solutions for businesses and organizations: web apps, CRM/ERP dashboards, document automation, legal tech, mobile apps and secure cloud infrastructure.",
    heroDescHtml: false,
    heroTagline: "From business need to a secure, production-ready product.",
    heroServices: [
      { label: "Custom apps", href: "/custom-software" },
      { label: "AI automations", href: "/ai-automations" },
      { label: "CRM / ERP dashboards", href: "/custom-software" },
      { label: "Invoice automation", href: "/invoice-automation" },
      { label: "Legal tech", href: "/legal-tech" },
      { label: "Mobile apps", href: "/mobile-apps" },
    ],
    heroCta1: "Request a proposal",
    heroCta2: "See project examples",
    heroTechLabel: "Technologies I use",
    // About
    aboutTitle: "Why Orion Intelligence?",
    aboutP1: "At Orion Intelligence, I help businesses, professionals and teams get their own software instead of forcing their workflow into generic tools.",
    aboutP2: "I build MVPs, internal dashboards, AI automations, invoice recognition, legal tech products and cloud infrastructure. Based in Halkidiki, working remotely across Greece and Europe.",
    cardWebAppsTitle: "Custom Web Apps",
    cardWebAppsText: "SaaS platforms, Dashboards, CRM/ERP systems tailored to your needs.",
    cardAiTitle: "AI & Automation",
    cardAiText: "AI model integration, Chatbots, workflow automation and data analysis.",
    cardCloudTitle: "Cloud Infra & Security",
    cardCloudText: "Secure infrastructure on AWS/Google Cloud, GDPR compliance and data protection.",
    cardMobileTitle: "Mobile Solutions",
    cardMobileText: "Native and Cross-platform applications delivering top-tier user experience.",
    specTitle: "Industry Expertise",
    specLegalTitle: "Legal Tech", specLegalText: "Law firm management, Lawyer CRM, data security.",
    specSupplyTitle: "Supply Chain & Retail", specSupplyText: "Inventory management, B2B ordering.",
    specHealthTitle: "Health & Sports", specHealthText: "Performance tracking, medical data.",
    specRealEstateTitle: "Real Estate", specRealEstateText: "Property management, automated leasing.",
    // Products
    productsTitle: "Our Products",
    caseStudiesTitle: "Case studies",
    caseStudiesDesc: "Project examples and use cases showing how Orion Intelligence turns business needs into software, AI automations and cloud products.",
    caseStudyCards: [
      {
        title: "CaseMaster legal tech platform",
        text: "Case, client and deadline organization for law firms with secure cloud architecture.",
        href: "/case-studies/casemaster-legal-tech",
        tag: "Legal tech",
      },
      {
        title: "Invoice Intelligence automation",
        text: "AI invoice reading, data extraction and ERP-ready JSON for businesses and ERP providers.",
        href: "/case-studies/invoice-intelligence",
        tag: "Invoice automation",
      },
      {
        title: "AI legal workflow",
        text: "AI workflows for request classification, document processing and internal team organization.",
        href: "/case-studies/ai-legal-workflow",
        tag: "AI automation",
      },
    ],
    blogTitle: "Blog",
    blogDesc: "Short articles explaining when custom software makes sense, how AI automations help and how businesses can organize workflows better.",
    blogCta: "See all articles",
    cmDesc: "The ultimate management solution for legal professionals. Organize cases, clients and deadlines with the power of AI. Designed for the modern law firm.",
    laDesc: "The platform redefining how legal services are found. Instant connection between citizens and specialized lawyers. Online appointments and reviews.",
    themisDesc: "Source-grounded Legal AI Drafting SaaS. Reads court decisions, case files and DOCX templates to draft Authorizations, Title Research Reports & Pleadings with full provenance.",
    ipDesc: "A flexible platform for associations and member organizations: memberships, subscriptions, programs, attendance, documents, announcements and operational reporting in one secure environment.",
    ipCaseStudy: "Live implementation: Ippokampos Association for People with Disabilities",
    pvDesc: "Pro-grade race strategy and fueling for runners. Pace splits, hour-by-hour fueling plans with brand-specific gels, heart-rate zones, and a lock-screen wallpaper with your splits. Available now on Google Play.",
    // Trust
    trustTitle: "Trust & Security",
    trustDesc: "Privacy-first, least-privilege, encryption and transparent policies. Payments via Stripe — we never store card details.",
    trustTerms: "Terms of Service",
    trustPrivacy: "Privacy Policy",
    trustGdprTitle: "GDPR Compliance", trustGdprText: "Data collection/processing on lawful bases. Data subject rights tools.",
    trustGdprDetails: ["Consent management", "Data export/deletion", "Transparency & documentation"],
    trustPayTitle: "Secure Payments", trustPayText: "Payments are processed by Stripe. We never store card details.",
    trustPayDetails: ["PCI-DSS compliance", "3D Secure everywhere", "Signed webhooks"],
    trustDataTitle: "Data Security", trustDataText: "Firestore Rules, access control and encryption.",
    trustDataDetails: ["Least-privilege (RBAC)", "Encryption at rest/transit", "Audit logs & monitoring"],
    trustPrivacyTitle: "Privacy by Default", trustPrivacyText: "Data minimization & clear user settings.",
    trustPrivacyDetails: ["Privacy-first UI/flows", "Anonymized analytics", "User control settings"],
    trustReliabilityTitle: "Reliability & Monitoring", trustReliabilityText: "Health checks, alerts and backup strategy.",
    trustReliabilityDetails: ["Backups (Exports)", "Staging environments", "24/7 Monitoring"],
    trustRoadmapTitle: "Compliance Roadmap", trustRoadmapText: "Regular reviews and best practices.",
    trustRoadmapDetails: ["Policy reviews", "Partner training", "Security code reviews"],
    // Contact
    contactTitle: "Contact",
    contactLocation: "Polygyros, Halkidiki — Remote-first collaborations across Greece & Europe",
    contactGdprNote: "Your messages are not stored for marketing without consent. They will be used exclusively to respond to your request.",
    formName: "Full Name",
    formNamePlaceholder: "e.g. John Doe",
    formEmail: "Email",
    formMessage: "Message",
    formMessagePlaceholder: "Tell us a few words about what you need…",
    formSubmit: "Send Message",
    formSubmitting: "Sending...",
    formSuccess: "✅ Received! We'll get back to you soon.",
    formError: "❌ Something went wrong. Please try again.",
    // Validation
    valName: "Please fill in your name.",
    valEmail: "Please enter a valid email.",
    valMessage: "Please provide more details (≥10 characters).",
    valBot: "Something went wrong. Please try again.",
    // Footer
    footerFollow: "Follow us",
  },
};

export default function OrionLanding() {
  useSmoothScroll();
  useScrollToHashOnMount();

  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("el");
  const t = dict[lang];
  const topRef = useRef(null);

  // sections για scroll spy
  const sections = ["about", "certifications", "products", "trust-security", "contact"];
  const active = useScrollSpy(sections, {
    rootMargin: "-45% 0px -45% 0px",
    threshold: 0.01,
  });



  // Contact form state
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    hp: "",
  }); // hp = honeypot
  const [submitting, setSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const onChangeForm = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validateForm = () => {
    if (!form.name.trim()) return t.valName;
    if (!emailRegex.test(form.email)) return t.valEmail;
    if (form.message.trim().length < 10)
      return t.valMessage;
    if (form.hp?.trim()) return t.valBot;
    return null;
  };

  const onSubmitContact = async (e) => {
    e.preventDefault();

    if (submitting) return;          // anti double click
    setSubmitOk(null);

    const err = validateForm();
    if (err) {
      alert(err);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000); // 12s

    try {
      setSubmitting(true);

      const resp = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
        signal: controller.signal,
      });

      let data = null;
      try {
        data = await resp.json();
      } catch {
        data = null;
      }

      if (!resp.ok || (data && data.ok === false)) {
        const msg =
          (data && (data.error || data.message)) ||
          `Request failed (${resp.status})`;
        throw new Error(msg);
      }

      setSubmitOk(true);
      trackEvent("generate_lead", {
        method: "contact_form",
        form_name: "main_contact_form",
      });
      setForm({ name: "", email: "", message: "", hp: "" });
    } catch (err) {
      console.error("Contact error:", err);
      setSubmitOk(false);

      // optional: αν θες να δείχνεις και μήνυμα στο UI αντί για alert
      // alert(err?.message || "Κάτι πήγε στραβά. Δοκίμασε ξανά.");
    } finally {
      clearTimeout(timeout);
      setSubmitting(false);
    }
  };




  return (
    <div
      ref={topRef}
      id="top"
      className="min-h-screen text-white overflow-x-hidden overflow-y-auto"
      style={{
        background: `radial-gradient(circle at 15% 15%, rgba(25, 230, 255, 0.1), transparent 40%),
                     radial-gradient(circle at 85% 15%, rgba(192, 132, 252, 0.1), transparent 40%),
                     linear-gradient(${colors.bgTop}, ${colors.bgBottom})`,
      }}
    >
      {/* === Nav === */}
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-slate-950/85 shadow-2xl shadow-black/20 backdrop-blur-2xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 py-3">
          <a href="#top" className="flex shrink-0 items-center gap-3">
            <motion.img
              src="/assets/orion-logo-800.png"
              alt="Orion badge"
              className="w-8 h-8 md:w-9 md:h-9 rounded-md object-contain"
              loading="eager"
              animate={{
                boxShadow: ["0 0 0 2px rgba(25,230,255,0.15)", "0 0 15px 4px rgba(25,230,255,0.3)", "0 0 0 2px rgba(25,230,255,0.15)"]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              style={{ boxShadow: "0 0 0 2px rgba(25,230,255,0.15)" }}
            />
            <div className="leading-tight min-w-0">
              <div
                className="text-sm lg:text-base font-bold tracking-wide whitespace-nowrap"
                style={{ color: colors.primary }}
              >
                Orion Intelligence
              </div>
              <div className="text-[11px] lg:text-xs opacity-70 whitespace-nowrap">
                Apps • AI • Cloud
              </div>
            </div>
          </a>

          <div className="hidden xl:flex items-center justify-end gap-1.5 rounded-2xl border border-white/10 bg-white/[0.03] p-1.5">
            {[
              ["about", t.navAbout],
              ["certifications", t.navCertifications],
              ["products", t.navProducts],
              ["case-studies", t.navCaseStudies],
              ["blog", t.navBlog],
              ["trust-security", t.navTrust],
              ["contact", t.navContact],
            ].map(([id, label]) => (
              <motion.a
                key={id}
                href={`#${id}`}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.95 }}
                className={`inline-flex h-11 items-center justify-center rounded-xl px-3.5 text-sm font-bold leading-none whitespace-nowrap transition-all duration-300
                  ${active === id
                    ? "bg-[#19E6FF] text-[#001018] shadow-lg shadow-[#19E6FF]/25"
                    : "text-slate-200/85 hover:bg-white/[0.08] hover:text-white"
                  }`}
                aria-current={active === id ? "page" : undefined}
              >
                {label}
              </motion.a>
            ))}
            <button
              onClick={() => setLang(lang === "el" ? "en" : "el")}
              className="ml-1 inline-flex h-11 items-center justify-center rounded-xl border px-3 text-xs font-bold tracking-wider transition-colors hover:bg-white/10"
              style={{ borderColor: colors.cardBorder, color: colors.primary }}
              aria-label="Switch language"
            >
              {lang === "el" ? "EN" : "GR"}
            </button>
          </div>

          <div className="xl:hidden flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "el" ? "en" : "el")}
              className="px-2.5 py-1.5 rounded-lg border text-xs font-bold tracking-wider transition-colors"
              style={{ borderColor: colors.cardBorder, color: colors.primary }}
              aria-label="Switch language"
            >
              {lang === "el" ? "EN" : "GR"}
            </button>
            <button
              onClick={() => setOpen(!open)}
              className="p-2 rounded-lg border"
              style={{ borderColor: colors.cardBorder }}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            className="xl:hidden border-t bg-slate-950/95 backdrop-blur-2xl"
            style={{ borderColor: colors.cardBorder }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 grid gap-2 sm:grid-cols-2">
              {[
                ["about", t.navAbout],
                ["certifications", t.navCertifications],
                ["products", t.navProducts],
                ["case-studies", t.navCaseStudies],
                ["blog", t.navBlog],
                ["trust-security", t.navTrust],
                ["contact", t.navContact],
              ].map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border px-5 py-3 text-sm font-bold
                    ${active === id ? "bg-cyan-400 text-slate-950" : "bg-white/[0.03] text-white"}`}
                  style={{ borderColor: colors.cardBorder }}
                  aria-current={active === id ? "page" : undefined}
                  onClick={() => setOpen(false)}
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>


      {/* === Hero === */}
      <Section className="pt-28 md:pt-32 pb-8 relative">
        {/* Animated Grid Background */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(${colors.primary} 1px, transparent 1px), linear-gradient(90deg, ${colors.primary} 1px, transparent 1px)`,
            backgroundSize: "60px 60px"
          }}
        />

        {/* Decorative Hero Glow */}
        <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-cyan-500/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute top-1/3 right-1/4 translate-x-1/2 w-64 h-64 bg-purple-500/10 blur-[100px] rounded-full pointer-events-none" />

        <div className="grid lg:grid-cols-[1.2fr,0.8fr] gap-12 items-center relative z-10">
          {/* LEFT: copy + CTA */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 text-xs font-semibold mb-6">
                <Sparkles size={13} className="shrink-0" />
                <span className="tracking-wide pr-1">{t.heroBadge}</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-black leading-tight">
                <span>
                  {t.heroTitle1}
                  <span style={{ color: colors.primary }}>{t.heroTitle2}</span>
                </span>
              </h1>
            </motion.div>

            <p className="mt-4 text-sm md:text-base leading-relaxed" style={{ color: colors.textMuted }}>
              {t.heroDesc}
            </p>

            <p className="mt-4 text-sm md:text-base font-semibold" style={{ color: colors.primary }}>
              {t.heroTagline}
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              {t.heroServices.map((service) => (
                <Link
                  key={service.label}
                  to={service.href}
                  className="rounded-full border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: colors.cardBorder,
                    background: "rgba(255,255,255,0.04)",
                    color: colors.text,
                  }}
                >
                  {service.label}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl font-bold shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 transition-shadow"
                style={{ background: colors.primary, color: "#001018" }}
              >
                {t.heroCta1} <ArrowRight size={18} />
              </a>

              <a
                href="#products"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl border transition hover:bg-white/5"
                style={{ borderColor: colors.cardBorder }}
              >
                {t.heroCta2}
              </a>
            </div>

            {/* Tech Stack Strip */}
            <div className="mt-8 pt-6 border-t border-white/5">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-4">
                {t.heroTechLabel}
              </p>
              <div className="flex flex-wrap gap-6 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
                {/* Simple text or icons representations for now */}
                {['React', 'Node.js', 'Python', 'AWS', 'Docker', 'TensorFlow'].map((tech) => (
                  <span key={tech} className="text-sm font-semibold text-slate-400 hover:text-cyan-400 cursor-default transition-colors">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: logo + cards */}
          <div className="relative grid gap-4 max-w-full">
            <div className="flex justify-center lg:justify-end">
              <div
                className="rounded-2xl p-3 max-w-full"
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
                }}
              >
                <motion.div
                  animate={{
                    filter: ["drop-shadow(0 0 10px rgba(25,230,255,0.1))", "drop-shadow(0 0 30px rgba(25,230,255,0.4))", "drop-shadow(0 0 10px rgba(25,230,255,0.1))"]
                  }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <HeroVisual
                    size={440}
                    variant="final"
                    src="/assets/orion-logo-800.png"
                  />
                </motion.div>
              </div>

            </div>
          </div>
        </div>
      </Section>


      {/* === About === */}
      <Section id="about" className="py-8">
        <h2 className="text-xl md:text-3xl font-extrabold">{t.aboutTitle}</h2>
        <div
          className="mt-4 text-sm md:text-base max-w-3xl space-y-4"
          style={{ color: colors.textMuted }}
        >
          <p>{t.aboutP1}</p>
          <p>{t.aboutP2}</p>

          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <Link to="/custom-software" className="p-4 rounded-xl bg-white/5 border border-white/10 block hover:bg-white/10 transition-colors">
              <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                <Globe size={18} className="text-cyan-400" /> {t.cardWebAppsTitle}
              </h4>
              <p className="text-sm">{t.cardWebAppsText}</p>
            </Link>
            <Link to="/ai-automations" className="p-4 rounded-xl bg-white/5 border border-white/10 block hover:bg-white/10 transition-colors">
              <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                <Sparkles size={18} className="text-purple-400" /> {t.cardAiTitle}
              </h4>
              <p className="text-sm">{t.cardAiText}</p>
            </Link>
            <Link to="/cloud-apps" className="p-4 rounded-xl bg-white/5 border border-white/10 block hover:bg-white/10 transition-colors">
              <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                <ShieldCheck size={18} className="text-emerald-400" /> {t.cardCloudTitle}
              </h4>
              <p className="text-sm">{t.cardCloudText}</p>
            </Link>
            <Link to="/mobile-apps" className="p-4 rounded-xl bg-white/5 border border-white/10 block hover:bg-white/10 transition-colors">
              <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                <Phone size={18} className="text-amber-400" /> {t.cardMobileTitle}
              </h4>
              <p className="text-sm">{t.cardMobileText}</p>
            </Link>
          </div>


          <h3 className="mt-8 font-semibold text-white">{t.specTitle}</h3>
          <div className="grid sm:grid-cols-2 gap-6 mt-4">
            {[
              { icon: ShieldCheck, color: "text-cyan-400", title: t.specLegalTitle, text: t.specLegalText },
              { icon: MapPin, color: "text-emerald-400", title: t.specSupplyTitle, text: t.specSupplyText },
              { icon: Sparkles, color: "text-purple-400", title: t.specHealthTitle, text: t.specHealthText },
              { icon: Globe, color: "text-blue-400", title: t.specRealEstateTitle, text: t.specRealEstateText }
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ x: 5 }}
                className="flex items-start gap-4 p-4 rounded-2xl border border-white/5 bg-white/[0.02]"
              >
                <div className={`mt-1 ${item.color} p-2 rounded-lg bg-white/5`}>
                  <item.icon size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-white">{item.title}</h4>
                  <p className="text-xs mt-1 text-slate-400 leading-relaxed">{item.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* === Certifications === */}
      <div
        className="w-full h-px"
        style={{ background: "linear-gradient(90deg, transparent, rgba(25,230,255,0.3), transparent)" }}
      />
      <CertificationsSection lang={lang} />

      {/* === Products === */}
      <Section id="products" className="py-12">
        <h2 className="text-xl md:text-3xl font-extrabold mb-8">{t.productsTitle}</h2>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* CaseMaster Card */}
          <motion.div
            whileHover={{ y: -10, scale: 1.01 }}
            className="rounded-3xl border p-6 sm:p-8 relative overflow-hidden group shadow-2xl shadow-cyan-500/5"
            style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <span className="p-2 rounded-xl bg-cyan-500/20 text-cyan-400">
                  <Sparkles size={24} />
                </span>
                <h3 className="text-2xl font-black tracking-tight">CaseMaster</h3>
              </div>
              <p
                className="text-sm md:text-base mb-6"
                style={{ color: colors.textMuted }}
              >
                {t.cmDesc}
              </p>
              <div className="flex gap-3 flex-wrap items-center">
                <a
                  href="/casemaster"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl font-bold shadow-lg shadow-cyan-500/20 hover:bg-cyan-500/10 transition-colors"
                  style={{ background: colors.primary, color: "#001018" }}
                >
                  Δείτε το προϊόν
                  <ArrowRight size={16} />
                </a>
                <span
                  className="text-xs font-bold px-3 py-1.5 rounded-xl border border-cyan-500/50 bg-cyan-500/10 text-cyan-400"
                >
                  Δεν διατίθεται προς αγορά
                </span>
              </div>
            </div>
          </motion.div>

          {/* Lawyer Anywhere Card */}
          <motion.div
            whileHover={{ y: -10, scale: 1.01 }}
            className="rounded-3xl border p-8 relative overflow-hidden group shadow-2xl shadow-purple-500/5"
            style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <span className="p-2 rounded-xl bg-purple-500/20 text-purple-400">
                  <Globe size={24} />
                </span>
                <h3 className="text-2xl font-black tracking-tight">Lawyer Anywhere</h3>
              </div>
              <p
                className="text-sm md:text-base mb-6"
                style={{ color: colors.textMuted }}
              >
                {t.laDesc}
              </p>

              {/* App Preview Image */}
              <div className="mb-6 rounded-xl overflow-hidden border border-white/10">
                <img
                  src="/assets/lawyeranywhere.png"
                  alt="Lawyer Anywhere app preview"
                  className="w-full h-32 object-cover object-top opacity-80 group-hover:opacity-100 transition-opacity"
                />
              </div>

              <div className="flex gap-3 flex-wrap items-center">
                <a
                  href="/lawyer-anywhere"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl font-bold shadow-lg shadow-purple-500/20"
                  style={{ background: colors.accentPurple, color: "#001018" }}
                >
                  Get Early Access
                  <ArrowRight size={16} />
                </a>
                <span
                  className="text-xs font-bold px-3 py-1.5 rounded-xl border border-emerald-500/50 bg-emerald-500/10 text-emerald-400"
                >
                  Launching Soon
                </span>
                <span className="text-[10px] font-bold px-2 py-1 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  Offer: 1st Year Free
                </span>
              </div>
            </div>
          </motion.div>

          {/* Themis Cloud AI Card */}
          <motion.div
            whileHover={{ y: -10, scale: 1.01 }}
            className="rounded-3xl border p-6 sm:p-8 relative overflow-hidden group shadow-2xl shadow-amber-500/5 lg:col-span-2"
            style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="relative z-10 grid md:grid-cols-[1fr,0.6fr] gap-8 items-center">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <span className="p-2 rounded-xl bg-amber-500/20 text-amber-400">
                    <ShieldCheck size={24} />
                  </span>
                  <h3 className="text-2xl font-black tracking-tight">Themis Cloud AI</h3>
                </div>
                <p className="text-sm md:text-base mb-6" style={{ color: colors.textMuted }}>
                  {t.themisDesc}
                </p>
                <div className="flex gap-3 flex-wrap items-center">
                  <a
                    href="https://themis.orionintelligence.net"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl font-bold shadow-lg shadow-amber-500/20"
                    style={{ background: "#d6b16f", color: "#0b1d33" }}
                  >
                    Άνοιγμα Πλατφόρμας
                    <ArrowRight size={16} />
                  </a>
                  <a
                    href="/themis"
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl font-bold border border-amber-400/40 text-amber-300 hover:bg-amber-500/10 transition-colors text-sm"
                  >
                    Παρουσίαση Προϊόντος
                  </a>
                  <span className="text-xs font-bold px-3 py-1.5 rounded-xl border border-emerald-500/50 bg-emerald-500/10 text-emerald-400">
                    Live Cloud SaaS • 50k Users Ready
                  </span>
                </div>
              </div>
              <div className="hidden md:block opacity-40 group-hover:opacity-100 transition-opacity">
                <ShieldCheck size={170} className="text-amber-500/20" />
              </div>
            </div>
          </motion.div>

          {/* Ippokampos Card */}
          <motion.div
            whileHover={{ y: -10, scale: 1.01 }}
            className="rounded-3xl border p-6 sm:p-8 relative overflow-hidden group shadow-2xl shadow-emerald-500/5 lg:col-span-2"
            style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            <div className="relative z-10 grid md:grid-cols-[1fr,0.6fr] gap-8 items-center">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <span className="p-2 rounded-xl bg-emerald-500/20 text-emerald-400">
                    <Waves size={24} />
                  </span>
                  <h3 className="text-2xl font-black tracking-tight">Ippokampos</h3>
                </div>
                <p
                  className="text-sm md:text-base mb-6"
                  style={{ color: colors.textMuted }}
                >
                  {t.ipDesc}
                </p>
                <div className="flex gap-3 flex-wrap items-center">
                  <a
                    href="/ippokampos"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl font-bold shadow-lg shadow-emerald-500/20"
                    style={{ background: colors.accentGreen, color: "#001018" }}
                  >
                    Learn More
                    <ArrowRight size={16} />
                  </a>
                  <span className="text-xs font-bold px-3 py-1.5 rounded-xl border border-emerald-500/50 bg-emerald-500/10 text-emerald-400">
                    For NPOs & Clubs
                  </span>
                </div>
                <a
                  href="https://ippokampos-amea.gr"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-emerald-300 hover:text-emerald-100"
                >
                  {t.ipCaseStudy}
                  <ArrowRight size={16} />
                </a>
              </div>
              <div className="hidden md:block opacity-40 group-hover:opacity-100 transition-opacity">
                <Globe size={180} className="text-emerald-500/20" />
              </div>
            </div>
          </motion.div>

          {/* PaceVibe Card */}
          <motion.div
            whileHover={{ y: -10, scale: 1.01 }}
            className="rounded-3xl border p-8 relative overflow-hidden group shadow-2xl shadow-lime-500/5 lg:col-span-2"
            style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-lime-300/10 via-transparent to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            <div className="absolute top-5 right-5 flex items-center gap-2 rounded-full px-3 py-1 border"
              style={{
                background: "rgba(223, 255, 0, 0.08)",
                borderColor: "rgba(223, 255, 0, 0.3)"
              }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#DFFF00" }} />
              <span className="text-xs font-bold" style={{ color: "#DFFF00" }}>Live on Google Play</span>
            </div>

            <div className="relative z-10 grid md:grid-cols-[1fr,0.5fr] gap-8 items-center">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <span className="p-2 rounded-xl flex items-center justify-center"
                    style={{ background: "rgba(223, 255, 0, 0.15)", color: "#DFFF00" }}>
                    <Activity size={24} />
                  </span>
                  <h3 className="text-2xl font-black tracking-tight italic">
                    Pace<span style={{ color: "#DFFF00" }}>Vibe</span>
                  </h3>
                </div>
                <p
                  className="text-sm md:text-base mb-6"
                  style={{ color: colors.textMuted }}
                >
                  {t.pvDesc}
                </p>
                <div className="flex gap-3 flex-wrap items-center">
                  <Link
                    to="/pacevibe"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl font-bold shadow-lg transition-transform hover:scale-105"
                    style={{
                      background: "#DFFF00",
                      color: "#0F1115",
                      boxShadow: "0 10px 30px rgba(223, 255, 0, 0.2)"
                    }}
                  >
                    Μάθε περισσότερα
                    <ArrowRight size={16} />
                  </Link>
                  <span className="text-xs font-bold px-3 py-1.5 rounded-xl border"
                    style={{
                      borderColor: "rgba(255, 87, 34, 0.5)",
                      background: "rgba(255, 87, 34, 0.1)",
                      color: "#FF8A65"
                    }}>
                    For Runners
                  </span>
                  <span className="text-xs font-bold px-3 py-1.5 rounded-xl border"
                    style={{
                      borderColor: "rgba(223, 255, 0, 0.4)",
                      background: "rgba(223, 255, 0, 0.06)",
                      color: "#DFFF00"
                    }}>
                    Free + Pro
                  </span>
                </div>
              </div>
              <div className="hidden md:flex justify-center items-center opacity-90 group-hover:opacity-100 transition-opacity">
                <div className="relative">
                  <div className="absolute inset-0 blur-2xl rounded-full"
                    style={{ background: "rgba(223, 255, 0, 0.2)" }} />
                  <img
                    src="/assets/pacevibe-logo.jpg"
                    alt="PaceVibe app icon"
                    className="relative w-32 h-32 rounded-3xl object-cover border-2"
                    style={{
                      borderColor: "rgba(223, 255, 0, 0.3)",
                      boxShadow: "0 20px 50px rgba(223, 255, 0, 0.15)"
                    }}
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Invoice Intelligence Demo Card */}
          <motion.div
            whileHover={{ y: -10, scale: 1.01 }}
            className="rounded-3xl border p-8 relative overflow-hidden group shadow-2xl shadow-indigo-500/5 lg:col-span-3"
            style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="absolute top-5 right-5 flex items-center gap-2 bg-indigo-500/10 border border-indigo-400/20 rounded-full px-3 py-1">
              <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
              <span className="text-xs font-bold text-indigo-300">AI-Powered · B2B API</span>
            </div>
            <div className="relative z-10 grid md:grid-cols-[1fr,auto] gap-8 items-center">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <img 
                    src="/assets/invoice_icon.png" 
                    alt="Invoice Intelligence Icon" 
                    className="w-14 h-14 rounded-2xl shadow-lg shadow-indigo-500/20 border border-indigo-500/30 object-cover" 
                  />
                  <h3 className="text-2xl font-black tracking-tight">Invoice Intelligence</h3>
                </div>
                <p className="text-sm md:text-base mb-6" style={{ color: colors.textMuted }}>
                  AI ανάλυση τιμολογίων σε δευτερολέπτα. Αυτόματη εξαγωγή vendor, ημερομηνίας, γραμμών προϊόντων και ΦΠΑ — έτοιμα για ERP ενσωμάτωση. Διαθέσιμο ως API για developers και ERP providers.
                </p>
                <div className="flex gap-3 flex-wrap items-center">
                  <Link
                    to="/invoice-demo"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl font-bold shadow-lg shadow-indigo-500/20"
                    style={{ background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff" }}
                  >
                    Δείτε το Demo
                    <ArrowRight size={16} />
                  </Link>
                  <span className="text-xs font-bold px-3 py-1.5 rounded-xl border border-indigo-500/40 bg-indigo-500/10 text-indigo-300">PDF · JPG · PNG</span>
                  <span className="text-xs font-bold px-3 py-1.5 rounded-xl border border-blue-500/40 bg-blue-500/10 text-blue-300">ERP-ready JSON</span>
                </div>
              </div>
              <div className="hidden md:flex flex-col gap-3 opacity-60 group-hover:opacity-100 transition-opacity">
                {["Vendor & ημερομηνία", "Γραμμές προϊόντων", "ΦΠΑ & σύνολα", "ERP-ready output"].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm" style={{ color: colors.textMuted }}>
                    <span className="w-4 h-4 rounded-full bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-400" style={{ fontSize: 9 }}>✓</span>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

        </div>
      </Section>

      {/* === Case Studies === */}
      <Section id="case-studies" className="py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-xl md:text-3xl font-extrabold">{t.caseStudiesTitle}</h2>
            <p className="mt-3 max-w-3xl text-sm md:text-base leading-relaxed" style={{ color: colors.textMuted }}>
              {t.caseStudiesDesc}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {t.caseStudyCards.map((study, index) => {
            const icons = [ShieldCheck, Sparkles, Activity];
            const Icon = icons[index] || Sparkles;
            return (
              <motion.div
                key={study.href}
                whileHover={{ y: -6 }}
                className="rounded-2xl border p-6 relative overflow-hidden"
                style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-cyan-400 via-purple-400 to-emerald-300" />
                <div className="flex items-center justify-between gap-3">
                  <span className="rounded-xl bg-cyan-400/10 p-3 text-cyan-300">
                    <Icon size={22} />
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-bold text-slate-300">
                    {study.tag}
                  </span>
                </div>
                <h3 className="mt-5 text-lg font-black leading-tight">{study.title}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: colors.textMuted }}>
                  {study.text}
                </p>
                <Link
                  to={study.href}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-cyan-300 hover:text-cyan-100"
                >
                  Read case study
                  <ArrowRight size={16} />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* === Blog === */}
      <Section id="blog" className="py-10">
        <div className="rounded-2xl border p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5" style={{ background: colors.cardBg, borderColor: colors.cardBorder }}>
          <div>
            <h2 className="text-xl md:text-3xl font-extrabold">{t.blogTitle}</h2>
            <p className="mt-3 max-w-3xl text-sm md:text-base leading-relaxed" style={{ color: colors.textMuted }}>
              {t.blogDesc}
            </p>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-3.5 font-bold shadow-lg shadow-cyan-500/20 shrink-0"
            style={{ background: colors.primary, color: "#001018" }}
          >
            {t.blogCta}
            <ArrowRight size={18} />
          </Link>
        </div>
      </Section>

      {/* === Trust & Security === */}
      <Section id="trust-security" className="py-16">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          variants={fmContainer}
        >
          {/* Header */}
          <motion.div variants={fmItem} className="text-center max-w-3xl mx-auto">
            <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
              <span
                className="inline-flex items-center rounded-full border px-3 py-1"
                style={{
                  borderColor: colors.cardBorder,
                  background: "rgba(255,255,255,0.06)",
                }}
              >
                GDPR Ready
              </span>
              <span
                className="inline-flex items-center rounded-full border px-3 py-1"
                style={{
                  borderColor: colors.cardBorder,
                  background: "rgba(255,255,255,0.06)",
                }}
              >
                Secure by Design
              </span>
              <span
                className="inline-flex items-center rounded-full border px-3 py-1"
                style={{
                  borderColor: colors.cardBorder,
                  background: "rgba(255,255,255,0.06)",
                }}
              >
                Stripe Payments
              </span>
            </div>

            <h2 className="mt-4 text-2xl md:text-4xl font-extrabold">
              {t.trustTitle}
            </h2>
            <p
              className="mt-3 text-sm md:text-base opacity-80"
              style={{ color: colors.textMuted }}
            >
              {t.trustDesc}
            </p>

            <div className="mt-4 text-sm flex items-center justify-center gap-2">
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full border"
                style={{
                  borderColor: colors.cardBorder,
                  background: "rgba(255,255,255,0.06)",
                }}
                aria-hidden="true"
              >
                <ShieldCheck size={12} />
              </span>
              <Link
                to="/terms"
                className="underline underline-offset-4 hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50 rounded-sm"
              >
                {t.trustTerms}
              </Link>
              <span className="opacity-60">•</span>
              <Link
                to="/privacy"
                className="underline underline-offset-4 hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50 rounded-sm"
              >
                {t.trustPrivacy}
              </Link>
            </div>
          </motion.div>

          {/* Grid Cards */}
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: ShieldCheck, title: t.trustGdprTitle, text: t.trustGdprText, details: t.trustGdprDetails },
              { icon: CreditCard, title: t.trustPayTitle, text: t.trustPayText, details: t.trustPayDetails },
              { icon: Globe, title: t.trustDataTitle, text: t.trustDataText, details: t.trustDataDetails },
              { icon: Lock, title: t.trustPrivacyTitle, text: t.trustPrivacyText, details: t.trustPrivacyDetails },
              { icon: Sparkles, title: t.trustReliabilityTitle, text: t.trustReliabilityText, details: t.trustReliabilityDetails },
              { icon: ShieldCheck, title: t.trustRoadmapTitle, text: t.trustRoadmapText, details: t.trustRoadmapDetails }
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fmItem}
                whileHover={{ y: -8, backgroundColor: "rgba(255,255,255,0.05)", borderColor: "rgba(25,230,255,0.2)" }}
                className="relative overflow-hidden rounded-[2rem] border p-8 backdrop-blur-md transition-all duration-300 group"
                style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-2xl bg-white/5 group-hover:bg-cyan-500/10 transition-colors">
                      <item.icon size={22} className="text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white tracking-tight">{item.title}</h3>
                      <p className="mt-1 text-xs leading-relaxed" style={{ color: colors.textMuted }}>{item.text}</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-[11px] opacity-70 group-hover:opacity-100 transition-opacity">
                    {item.details.map((detail, j) => (
                      <li key={j} className="flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full bg-cyan-400" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>


        </motion.div>
      </Section>
      <div
        className="w-full h-px my-16"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(25,230,255,0.4), transparent)",
        }}
      />


      {/* === Contact === */}
      <Section id="contact" className="py-20 relative">
        <h2 className="text-2xl md:text-3xl font-extrabold mb-8">{t.contactTitle}</h2>

        <div className="grid lg:grid-cols-[1fr,1fr] gap-12">
          {/* Info cards */}
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            {[
              { icon: Mail, label: "support@orionintelligence.net", href: "mailto:support@orionintelligence.net" },
              { icon: Phone, label: "+30 690 691 0914", href: "tel:+306906910914" }
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -5, backgroundColor: "rgba(255,255,255,0.05)" }}
                className="p-6 rounded-2xl border backdrop-blur-md transition-all duration-300"
                style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
              >
                <div className="flex items-center gap-3 opacity-90">
                  <item.icon size={18} className="text-cyan-400" />
                  <a href={item.href} className="underline underline-offset-4 hover:opacity-90 transition-opacity">
                    {item.label}
                  </a>
                </div>
              </motion.div>
            ))}

            <motion.div
              whileHover={{ backgroundColor: "rgba(255,255,255,0.05)" }}
              className="p-6 rounded-2xl border backdrop-blur-md sm:col-span-2 transition-all duration-300"
              style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
            >
              <div className="flex flex-col gap-2 opacity-90">
                <div className="flex items-center gap-3">
                  <ShieldCheck size={18} className="text-cyan-400" />
                  <span className="font-bold text-xs uppercase tracking-widest text-cyan-400">Στοιχεία Επιχείρησης</span>
                </div>
                <div className="text-[13px] grid gap-1 mt-2 text-slate-300 ml-7">
                  <div><strong>Επωνυμία:</strong> Δέλλιος Δημήτριος του Αθανασίου</div>
                  <div><strong>ΑΦΜ:</strong> 135602991 (Δ.Ο.Υ. Πολυγύρου)</div>
                  <div><strong>Γ.Ε.ΜΗ:</strong> 192625657000</div>
                  <div><strong>Έδρα:</strong> Πολύγυρος Χαλκιδικής, 63100</div>
                </div>
              </div>
            </motion.div>

            <div
              className="p-6 rounded-2xl border sm:col-span-2 mt-2"
              style={{ background: "rgba(25,230,255,0.03)", borderColor: "rgba(25,230,255,0.1)" }}
            >
              <div className="flex items-start gap-3 opacity-90">
                <ShieldCheck size={18} className="text-cyan-400 shrink-0 mt-1" />
                <p className="text-[13px] leading-relaxed" style={{ color: colors.textMuted }}>
                  {t.contactGdprNote}
                </p>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <motion.form
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            onSubmit={onSubmitContact}
            className="p-8 rounded-3xl border backdrop-blur-xl relative overflow-hidden group"
            style={{ background: colors.cardBg, borderColor: colors.cardBorder }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            <div className="grid gap-6 relative z-10">
              <label className="text-sm font-medium">
                {t.formName}
                <input
                  name="name"
                  value={form.name}
                  onChange={onChangeForm}
                  placeholder={t.formNamePlaceholder}
                  className="mt-2 w-full rounded-xl px-4 py-3 bg-white/5 border border-white/10 focus:border-cyan-500/50 focus:outline-none transition-colors"
                  autoComplete="name"
                  required
                />
              </label>

              <label className="text-sm font-medium">
                {t.formEmail}
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChangeForm}
                  placeholder="your@email.com"
                  className="mt-2 w-full rounded-xl px-4 py-3 bg-white/5 border border-white/10 focus:border-cyan-500/50 focus:outline-none transition-colors"
                  autoComplete="email"
                  required
                />
              </label>

              <label className="text-sm font-medium">
                {t.formMessage}
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChangeForm}
                  placeholder={t.formMessagePlaceholder}
                  rows={5}
                  className="mt-2 w-full rounded-xl px-4 py-3 bg-white/5 border border-white/10 focus:border-cyan-500/50 focus:outline-none transition-colors resize-none"
                  required
                />
              </label>

              {/* Honeypot */}
              <label className="hidden">
                <input name="hp" value={form.hp} onChange={onChangeForm} tabIndex={-1} autoComplete="off" />
              </label>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={submitting}
                className="mt-2 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-bold disabled:opacity-60 shadow-lg shadow-cyan-500/20"
                style={{ background: colors.primary, color: "#001018" }}
              >
                {submitting ? t.formSubmitting : t.formSubmit}
                <ArrowRight size={18} />
              </motion.button>

              {submitOk === true && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-sm rounded-xl px-4 py-3 border border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                >
                  {t.formSuccess}
                </motion.div>
              )}
              {submitOk === false && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-sm rounded-xl px-4 py-3 border border-red-500/30 text-red-400 bg-red-500/10"
                >
                  {t.formError}
                </motion.div>
              )}
            </div>
          </motion.form>
        </div>
      </Section>

      {/* === Footer === */}
      <footer className="py-12 border-t border-white/5 mt-20 relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Left — Logo */}
            <div className="flex items-center gap-3 grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all cursor-default">
              <img src="/assets/orion-logo-800.png" alt="Orion" className="w-6 h-6 rounded" />
              <span className="text-sm font-bold tracking-widest uppercase">Orion Intelligence</span>
            </div>

            {/* Center — Social Media */}
            <div className="flex flex-col items-center gap-3">
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-500">{t.footerFollow}</span>
              <SocialIcons size={16} gap="gap-2" />
            </div>

            {/* Right — Legal + Copyright */}
            <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-2 text-[10px] font-medium tracking-widest uppercase opacity-60">
              <Link to="/privacy" className="hover:text-cyan-400 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-cyan-400 transition-colors">Terms</Link>
              <span className="hidden md:inline text-slate-700">|</span>
              <span className="cursor-default">Γ.Ε.ΜΗ: 192625657000</span>
              <span className="cursor-default">© {new Date().getFullYear()}</span>
            </div>
          </div>
        </div>
      </footer>


    </div>
  );
}
